import { getVoucherIdsFromPromoPaymentResponse } from '~/utils/getVoucherIdsFromPromoPaymentResponse';

import type { SimulationResult } from './PromoSimulationResults';

export type CalculatorStatus = 'pending' | 'running' | 'complete' | 'error';

export type OrderRecord = Record<string, unknown>;
export type DiscountCalculationResult = SimulationResult;

export type SimulationRow = {
  id: string;
  orderId: string;
  orderNumber: string;
  createdAt: string;
  total: string;
  status: string;
  calculatorStatus: CalculatorStatus;
  promoCode: string;
  discount: string;
  vouchers: string;
  error: string;
  sourceOrder: OrderRecord;
  submittedCodeName?: string;
  submittedVoucherIds: Array<string>;
  result?: DiscountCalculationResult;
  debugData?: unknown;
};

function stringValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function getOrderVoucherIds(order: OrderRecord): Array<string> {
  const payments = Array.isArray(order.payments) ? order.payments : [];
  return Array.from(
    new Set(
      payments.flatMap((payment) => {
        if (!payment || typeof payment !== 'object' || Array.isArray(payment)) {
          return [];
        }
        const responseJson = (payment as { response_json?: unknown }).response_json;
        return getVoucherIdsFromPromoPaymentResponse(
          responseJson && typeof responseJson === 'object' && !Array.isArray(responseJson)
            ? (responseJson as Record<string, unknown>)
            : undefined,
        );
      }),
    ),
  );
}

function getOrderPromoCode(order: OrderRecord): string {
  const orderCode = stringValue(order.promo_code ?? order.promoCode ?? order.code_name);
  if (orderCode) {
    return orderCode;
  }
  const payments = Array.isArray(order.payments) ? order.payments : [];
  for (const payment of payments) {
    if (!payment || typeof payment !== 'object' || Array.isArray(payment)) {
      continue;
    }
    const responseJson = (payment as { response_json?: unknown }).response_json;
    if (!responseJson || typeof responseJson !== 'object' || Array.isArray(responseJson)) {
      continue;
    }
    const details = (responseJson as { details?: unknown }).details;
    const code = stringValue(
      (responseJson as Record<string, unknown>).promoCode ??
        (responseJson as Record<string, unknown>).promo_code ??
        (responseJson as Record<string, unknown>).code_name ??
        (details && typeof details === 'object' && !Array.isArray(details)
          ? (details as Record<string, unknown>).code_name
          : undefined),
    );
    if (code) {
      return code;
    }
  }
  return '';
}

export function getMultiSimulationInputs(
  order: OrderRecord,
  codeName: string,
  voucherIds: Array<string>,
  useOriginal: boolean,
) {
  return {
    codeName: codeName.trim() || (useOriginal ? getOrderPromoCode(order) : ''),
    voucherIds: voucherIds.length ? voucherIds : useOriginal ? getOrderVoucherIds(order) : [],
  };
}
